import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Images } from "../../../assets/common";
import { SocketContext } from "../../../context/socket";
import ApiService from "../../../services/api-service";
import { findViewerRow, formatSupportAmount, toSupporterRows } from "./supporters";

// Tips also arrive over the socket; the poll only covers a missed event.
const REFRESH_MS = 30000;

const CrownIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
    <path d="m2 7 5 5 5-8 5 8 5-5-2 12H4Z" />
  </svg>
);

/**
 * Top tippers for the current stream, ranked by total tipped (USD).
 * Styles live in pages/Streaming/live-layout.scss (`.live-supporters`).
 */
export default function LiveTopSupporters({ channelName, userId, isOwner, onTip }) {
  const { t } = useTranslation("common");
  const socket = useContext(SocketContext).Socket;
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!channelName) return;
    try {
      const response = await ApiService.TOP_TIPPERS(channelName);
      setRows(toSupporterRows(response?.result));
    } catch (error) {
      console.error("[LiveTopSupporters] failed to load top tippers", { channelName, error });
    } finally {
      setLoaded(true);
    }
  }, [channelName]);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!socket) return;
    socket.on("totalTips", load);
    return () => socket.off("totalTips", load);
  }, [socket, load]);

  const viewer = findViewerRow(rows, userId);

  return (
    <aside className="live-panel live-supporters" aria-label={t("stream.text2")}>
      <header className="live-panel__head">
        <h2><span className="live-supporters__crown"><CrownIcon /></span><span className="live-supporters__title">{t("stream.text2")}</span></h2>
      </header>

      <ol className="live-supporters__list">
        {rows.map((row) => (
          <li
            key={row.id}
            className={`live-supporters__row live-supporters__row--r${Math.min(row.rank, 4)}`}
            title={`#${row.rank} ${row.name} · ${formatSupportAmount(row.amount)}`}
          >
            <span className="live-supporters__rank">{row.rank}</span>
            <img src={row.avatar || Images.dummy} alt="" />
            <span className="live-supporters__name">{row.name}</span>
            <span className="live-supporters__amount">{formatSupportAmount(row.amount)}</span>
          </li>
        ))}
      </ol>

      {loaded && rows.length === 0 && (
        <p className="live-supporters__empty">No tips yet</p>
      )}

      {!isOwner && (
        <div className="live-supporters__you">
          <span className="live-supporters__you-rank">
            {viewer && <><b>#{viewer.rank}</b> · {formatSupportAmount(viewer.amount)}</>}
          </span>
          {onTip && (
            <button type="button" onClick={onTip}>Tip</button>
          )}
        </div>
      )}
    </aside>
  );
}
